import React from "react";
import { Drawer, Tabs, Descriptions, Table, Tag, Timeline, Button } from "antd";
import type { TabsProps } from "antd";
import { Printer, DollarSign } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TemporaryItemProduct } from "../../../Enums/TemporaryItem";
import { useCart } from "../../../Context/useCartContext";
import { ROUTE } from "../../../Constant";
import {
  RepairJob,
  RepairJobPayment,
  RepairJobStatusHistory,
} from "../../../Types/RepairJobTypes";
import { drawerTitle } from "../../../components/AppUi";
import { useSystemConfig } from "../../../Hook";
import { renderRepair } from "../../../components/templates";

interface RepairJobViewDrawerProps {
  open: boolean;
  onClose: () => void;
  job: RepairJob;
}

const statusColors: Record<string, string> = {
  pending: "orange",
  diagnosed: "blue",
  in_progress: "cyan",
  completed: "green",
  delivered: "purple",
  cancelled: "red",
};

const priorityColors: Record<string, string> = {
  low: "default",
  normal: "blue",
  high: "orange",
  urgent: "red",
};

const RepairJobViewDrawer: React.FC<RepairJobViewDrawerProps> = ({
  open,
  onClose,
  job,
}) => {
  const navigate = useNavigate();
  const {
    addToCart,
    setRepairJobId,
    setRepairJobAdvance,
    setRepairJobPaymentMethodId,
  } = useCart();
  const { storeName, address, phone, currency, receiptFooter } =
    useSystemConfig();

  const handleProceedToPay = () => {
    const deviceLabel =
      [job.device_brand, job.device_model].filter(Boolean).join(" ") ||
      job.device_type;
    const baseName = `Repair: ${deviceLabel} (#${job.job_number})`;
    const dueAmount = Number(job.due_amount) || 0;

    setRepairJobId(job.id);
    setRepairJobAdvance(0);
    setRepairJobPaymentMethodId(null);

    addToCart(
      {
        id: TemporaryItemProduct.PRODUCT,
        title: `${baseName}${dueAmount < Number(job.selling_price) ? " - Due Payment" : ""}`,
        image: "",
        is_tempory_item: true,
        is_repair_item: true,
        repair_job_id: job.id,
        is_balance: true,
      },
      {
        batch_no: `${TemporaryItemProduct.STOCK}-balance-${job.id}`,
        cost_price: Number(job.total_cost) || 0,
      },
      dueAmount,
      1,
      { is_tempory_item: true, is_repair_item: true },
      Number(job.total_cost) || 0,
      `Due payment for Job #${job.job_number}`,
    );

    onClose();
    navigate(`/system/${ROUTE.sales_billing}`);
  };

  const handlePrintTicket = () => {
    if (!job) return;

    const invoiceConfig = {
      store_name: storeName || "POS Store",
      address: address || "Store Address",
      phone: phone || "Store Phone",
      currency: currency || "LKR",
      receipt_footer: receiptFooter || "Thank you for choosing us!",
    };

    const htmlContent = renderRepair({
      job,
      config: invoiceConfig,
      pageSize: "80mm",
      isForPrint: true,
    });

    const frame = document.createElement("iframe");
    frame.style.cssText = "position:absolute;top:-9999px;left:-9999px;";
    document.body.appendChild(frame);

    const doc = frame.contentWindow?.document || frame.contentDocument;
    if (!doc) {
      if (frame.parentNode) {
        frame.parentNode.removeChild(frame);
      }
      return;
    }

    doc.open();
    doc.write(htmlContent);
    doc.close();

    window.setTimeout(() => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      window.setTimeout(() => {
        if (frame.parentNode) {
          frame.parentNode.removeChild(frame);
        }
      }, 1000);
    }, 500);
  };

  const paymentColumns = [
    {
      title: "Date",
      dataIndex: "payment_date",
      key: "payment_date",
      render: (v: string) => (v ? new Date(v).toLocaleDateString() : "-"),
    },
    { title: "Method", dataIndex: "payment_method_name", key: "method" },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v: number) => `Rs. ${Number(v).toFixed(2)}`,
    },
    { title: "Ref No", dataIndex: "transaction_no", key: "ref" },
    { title: "Notes", dataIndex: "notes", key: "notes" },
    { title: "Added By", dataIndex: "created_by_name", key: "created_by" },
  ];

  const items: TabsProps["items"] = [
    {
      key: "details",
      label: "Job Details",
      children: (
        <Descriptions column={2} bordered size="small" className="mt-4">
          <Descriptions.Item label="Job Number" span={2}>
            <span className="font-semibold">{job.job_number}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Customer" span={2}>
            {job.customer_name || job.temp_customer_name || "-"}
            {(job.customer_phone || job.temp_customer_phone) &&
              ` (${job.customer_phone || job.temp_customer_phone})`}
          </Descriptions.Item>
          <Descriptions.Item label="Device Type">
            {job.device_type}
          </Descriptions.Item>
          <Descriptions.Item label="Brand / Model">
            {[job.device_brand, job.device_model].filter(Boolean).join(" ") ||
              "-"}
          </Descriptions.Item>
          <Descriptions.Item label="IMEI / Serial">
            {job.device_imei || job.device_serial_number || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={statusColors[job.status]}>
              {job.status?.replace("_", " ").toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Priority">
            <Tag color={priorityColors[job.priority]}>
              {job.priority?.toUpperCase()}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Reported Issues" span={2}>
            {job.reported_issues}
          </Descriptions.Item>
          {job.diagnosis && (
            <Descriptions.Item label="Diagnosis" span={2}>
              {job.diagnosis}
            </Descriptions.Item>
          )}
          {job.technician_notes && (
            <Descriptions.Item label="Technician Notes" span={2}>
              {job.technician_notes}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Total Cost (Store Cost)">
            Rs. {Number(job.total_cost).toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Selling Price">
            <span className="font-semibold">
              Rs. {Number(job.selling_price).toFixed(2)}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Paid Amount">
            <span className="text-green-600 font-medium">
              Rs. {Number(job.paid_amount).toFixed(2)}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Due Amount">
            <span
              className={
                Number(job.due_amount) > 0 ? "text-red-600 font-medium" : ""
              }
            >
              Rs. {Number(job.due_amount).toFixed(2)}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Received">
            {job.received_date
              ? new Date(job.received_date).toLocaleDateString()
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Promised">
            {job.promised_date
              ? new Date(job.promised_date).toLocaleDateString()
              : "-"}
          </Descriptions.Item>
          {job.completed_date && (
            <Descriptions.Item label="Completed">
              {new Date(job.completed_date).toLocaleDateString()}
            </Descriptions.Item>
          )}
          {job.delivered_date && (
            <Descriptions.Item label="Delivered">
              {new Date(job.delivered_date).toLocaleDateString()}
            </Descriptions.Item>
          )}
          {job.technician_name && (
            <Descriptions.Item label="Technician">
              {job.technician_name}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Created By">
            {job.created_by_name || "-"}
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: "payments",
      label: `Payments (${job.payments?.length ?? 0})`,
      children: (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded p-3 text-center border">
              <div className="text-xs text-gray-500 uppercase tracking-wide">
                Selling Price
              </div>
              <div className="text-lg font-bold">
                Rs. {Number(job.selling_price).toFixed(2)}
              </div>
            </div>
            <div className="bg-green-50 rounded p-3 text-center border border-green-200">
              <div className="text-xs text-gray-500 uppercase tracking-wide">
                Paid Amount
              </div>
              <div className="text-lg font-bold text-green-700">
                Rs. {Number(job.paid_amount).toFixed(2)}
              </div>
            </div>
            <div className="bg-red-50 rounded p-3 text-center border border-red-200">
              <div className="text-xs text-gray-500 uppercase tracking-wide">
                Due Amount
              </div>
              <div
                className={`text-lg font-bold ${Number(job.due_amount) > 0 ? "text-red-600" : "text-green-600"}`}
              >
                Rs. {Number(job.due_amount).toFixed(2)}
              </div>
            </div>
          </div>
          <Table
            dataSource={job.payments ?? []}
            columns={paymentColumns}
            rowKey="id"
            pagination={false}
            size="small"
            bordered
            locale={{ emptyText: "No payments recorded" }}
          />
        </div>
      ),
    },
    {
      key: "history",
      label: "Status History",
      children: (
        <div className="mt-4 max-h-80 overflow-y-auto">
          {job.status_history?.length ? (
            <Timeline
              items={job.status_history.map((h: RepairJobStatusHistory) => ({
                children: (
                  <div>
                    <div className="flex items-center gap-2">
                      <Tag color={statusColors[h.new_status]}>
                        {h.new_status?.replace("_", " ").toUpperCase()}
                      </Tag>
                      {h.old_status && (
                        <span className="text-xs text-gray-400">
                          (was {h.old_status?.replace("_", " ")})
                        </span>
                      )}
                    </div>
                    {h.notes && (
                      <div className="text-sm text-gray-600 mt-1">
                        {h.notes}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      {h.changed_by_name && `by ${h.changed_by_name} `}
                      {h.created_at &&
                        `on ${new Date(h.created_at).toLocaleString()}`}
                    </div>
                  </div>
                ),
              }))}
            />
          ) : (
            <div className="text-gray-400 text-center py-8">
              No status history
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <Drawer
      title={drawerTitle(
        `Repair Job: ${job.job_number}`,
        `${job.device_type}${job.device_brand ? ` - ${job.device_brand}` : ""}`,
      )}
      placement="right"
      width={800}
      onClose={onClose}
      open={open}
      extra={
        <div className="flex gap-2">
          <Button icon={<Printer size={16} />} onClick={handlePrintTicket}>
            Print Ticket
          </Button>
          <Button
            type="primary"
            icon={<DollarSign size={16} />}
            onClick={handleProceedToPay}
            disabled={
              job.status === "completed" ||
              job.status === "cancelled" ||
              Number(job.due_amount) <= 0
            }
          >
            Proceed to Pay
          </Button>
        </div>
      }
    >
      <Tabs items={items} defaultActiveKey="details" />
    </Drawer>
  );
};

export default RepairJobViewDrawer;
